import { Link } from '@inertiajs/react'
import Seo from '../components/Seo'
import PackArtwork from '../components/PackArtwork'
import PackCard from '../components/PackCard'
import ComboArtwork from '../components/ComboArtwork'
import AddToCartButton from '../components/AddToCartButton'
import PreviewPlayer from '../components/PreviewPlayer'
import { useShared } from '../lib/hooks'
import { useAudio } from '../contexts/AudioPlayer'
import type { Pack, PackMember } from '../lib/types'

function MemberRow({ member }: { member: PackMember }) {
  const { play, isActive } = useAudio()
  const active = isActive(member.id)
  return (
    <div className="flex items-center gap-4 p-3 border border-line">
      <Link href={member.url} className="block size-16 shrink-0 overflow-hidden border border-line">
        <PackArtwork pack={{ name: member.name, image: member.image, catalog_number: null }} size="mid" />
      </Link>
      <div className="min-w-0 flex-1">
        <Link href={member.url} className="font-medium hover:text-paper">{member.name}</Link>
        <div className="text-xs text-muted mt-0.5">{member.price} value</div>
      </div>
      {member.audio_url && (
        <button
          type="button"
          onClick={() => play({ id: member.id, title: member.name, subtitle: 'Forma Sound', src: member.audio_url! })}
          aria-label={active ? `Pause ${member.name} preview` : `Play ${member.name} preview`}
          className="size-9 shrink-0 rounded-full bg-paper text-ink grid place-items-center hover:scale-105 transition"
        >
          {active ? (
            <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2.5h3v11H4zM9 2.5h3v11H9z" /></svg>
          ) : (
            <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2.5v11l9-5.5-9-5.5Z" /></svg>
          )}
        </button>
      )}
      <Link href={member.url} className="mono-label hover:text-paper transition-colors shrink-0">View →</Link>
    </div>
  )
}

export default function PackShow({
  pack,
  related,
}: {
  pack: Pack
  related: Pack[]
}) {
  const { site } = useShared()
  const origin = site.url
  const isCombo = pack.is_combo

  const productLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: pack.name,
    description: pack.description ?? pack.tagline ?? undefined,
    sku: `FS-${pack.id}`,
    category: pack.type_label,
    brand: { '@type': 'Brand', name: 'Forma Sound' },
    image: pack.image?.full ? `${origin}${pack.image.full}` : undefined,
    offers: {
      '@type': 'Offer',
      price: (pack.price_cents / 100).toFixed(2),
      priceCurrency: pack.currency,
      availability: 'https://schema.org/InStock',
      url: `${origin}${pack.url}`,
    },
  }
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Catalog', item: `${origin}/packs` },
      { '@type': 'ListItem', position: 2, name: pack.name, item: `${origin}${pack.url}` },
    ],
  }

  return (
    <>
      <Seo
        title={pack.name}
        description={pack.description ?? pack.tagline ?? undefined}
        image={pack.image?.full}
        type="product"
        jsonLd={[productLd, breadcrumbLd]}
      />

      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-8">
        <div className="mono-label mb-8">
          <Link href="/packs" className="hover:text-paper transition-colors">Catalog</Link>
          <span className="mx-2 text-line-2">/</span>
          <span className="text-paper">{pack.name}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-14">
          {/* Artwork */}
          <div>
            <div className="aspect-square border border-line overflow-hidden">
              {isCombo && !pack.has_image ? (
                <ComboArtwork members={pack.members} />
              ) : (
                <PackArtwork pack={pack} size="full" />
              )}
            </div>
          </div>

          {/* Details */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="flex items-center gap-3">
              {pack.catalog_number && <span className="mono-label !text-paper">{pack.catalog_number}</span>}
              <span className="mono-label">{pack.type_label}</span>
            </div>
            <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight">{pack.name}</h1>
            <div className="mt-2 text-muted">
              {isCombo
                ? `Includes ${pack.members.map((m) => m.name).join(' + ')}`
                : (<>{pack.manufacturer}{pack.plugin && <span> · for {pack.plugin}</span>}</>)}
            </div>

            <div className="mt-6 flex items-baseline gap-3">
              <span className={`text-3xl font-semibold tabular-nums ${pack.free ? 'text-emerald-400' : ''}`}>
                {pack.free ? 'Free' : pack.price}
              </span>
              {isCombo && pack.original_price && (
                <span className="text-xl text-muted line-through tabular-nums">{pack.original_price}</span>
              )}
              {isCombo && pack.savings && (
                <span className="mono-label !text-ink bg-emerald-400 px-2 py-1 rounded">Save {pack.savings}</span>
              )}
            </div>

            <div className="mt-6">
              <AddToCartButton pack={pack} className="w-full sm:w-auto" />
            </div>

            {!isCombo && pack.audio_url && (
              <div className="mt-8">
                <PreviewPlayer pack={pack} />
              </div>
            )}

            {pack.description && (
              <p className="mt-8 text-paper/80 leading-relaxed max-w-prose">{pack.description}</p>
            )}

            {pack.genres.length > 0 && (
              <div className="mt-8">
                <div className="mono-label mb-2">Genres</div>
                <div className="flex flex-wrap gap-2">
                  {pack.genres.map((g) => (
                    <span key={g} className="mono-label border border-line px-3 py-1.5">{g}</span>
                  ))}
                </div>
              </div>
            )}

            <dl className="mt-8 grid grid-cols-2 gap-px bg-line border border-line text-sm">
              <div className="bg-ink p-4">
                <dt className="mono-label">Format</dt>
                <dd className="mt-1">{pack.plugin ?? 'Serum 2'}</dd>
              </div>
              <div className="bg-ink p-4">
                <dt className="mono-label">Delivery</dt>
                <dd className="mt-1">Instant download</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* What's in the bundle */}
        {isCombo && pack.members.length > 0 && (
          <section className="mt-16 max-w-3xl">
            <h2 className="mono-label mb-4">What's included · {pack.members.length} packs</h2>
            <div className="space-y-3">
              {pack.members.map((m) => (
                <MemberRow key={m.id} member={m} />
              ))}
            </div>
          </section>
        )}

        {pack.youtube_id && (
          <section className="mt-16 max-w-3xl">
            <h2 className="mono-label mb-4">Walkthrough</h2>
            <div className="aspect-video border border-line overflow-hidden bg-ink-soft">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${pack.youtube_id}`}
                title={`${pack.name} walkthrough`}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-20">
            <h2 className="mono-label mb-5">More from Forma Sound</h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {related.map((p) => (
                <PackCard key={p.id} pack={p} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  )
}
